import React, { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { useCart } from "../../../context/CartContext";
import { useRefCodeManagement } from "../../../hooks/useRefCodeManagement";
import { 
    GraduationCap,  
    Award, 
    Check, 
    Zap,
    Calendar,
    Plane,
    Home,
    Shield,
    Bus,
    Utensils,
    PlaneLanding
} from "lucide-react";

interface TrainingProps {
    program2Weeks: any;
    program4Weeks: any;
}

const Training: React.FC<TrainingProps> = ({ program2Weeks, program4Weeks }) => {
    const { clearAndEnroll } = useCart();
    const navigate = useNavigate();
    const { navigateWithRefCode } = useRefCodeManagement();
    const [agreement1, setAgreement1] = useState(false);
    const [agreement2, setAgreement2] = useState(false);

    const handleEnrollNow = (program: any) => {
        clearAndEnroll(program, 1);
        navigate(navigateWithRefCode('/cart'));
    };

    // Check if we're showing the sub-program (cambridge-academic-innovation-summer-program)
    const isSubProgram = program2Weeks?.slug === "cambridge-academic-innovation-summer-program";
    
    const trainingPackages = [
        {
            duration: isSubProgram ? program2Weeks?.duration || "2 Weeks" : "2 Weeks",
            program: program2Weeks,
            icon: Zap,
            label: isSubProgram ? "Academic Innovation Program" : "Intensive Program",
            features: [
                { icon: Plane, text: "Flight" },
                { icon: Home, text: "Accommodation" },
                { icon: Shield, text: "Insurance" },
                { icon: GraduationCap, text: "Tuition" },
                { icon: Bus, text: "Travels" },
                { icon: Utensils, text: "3 Daily Meals" },
                { icon: PlaneLanding, text: "Airport Pick-up at UK" },
                { icon: Award, text: "Certificate & Recommendation Letter" },
            ],
        },
        {
            duration: program4Weeks?.duration || "4 Weeks",
            program: program4Weeks,
            icon: Calendar,
            label: "Comprehensive Program",
            features: [
                { icon: Plane, text: "Flight" },
                { icon: Home, text: "Accommodation" },
                { icon: Shield, text: "Insurance" },
                { icon: GraduationCap, text: "Tuition" },
                { icon: Bus, text: "Travels" },
                { icon: Utensils, text: "3 Daily Meals" },
                { icon: PlaneLanding, text: "Airport Pick-up at UK" },
                { icon: Award, text: "Certificate & Recommendation Letter" },
            ],
        },
    ];

    return (
        <div id="training-section" className="relative py-16 md:py-20 lg:py-24 overflow-hidden">
            {/* Background Image */}
            <div className="absolute inset-0">
                <img src="/images/cambridge-summer-school-training.jpg" alt="Cambridge University" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0000001A] via-[#000000CC] to-[#0000001A]"></div>
            </div>

            {/* Content */}
            <div className="relative z-10 max-w-7xl mx-auto px-4 overflow-visible">
                {/* Title */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="text-center mb-4 md:mb-6 lg:mb-8 overflow-visible"
                >
                    <h2 className="text-white text-md md:text-xl font-bold mb-2">Cambridge Summer</h2>
                    <h1 className="text-4xl md:text-[6rem] lg:text-[10rem] font-extrabold text-white leading-none pb-6 md:pb-8 lg:pb-12 overflow-visible">
                        Training
                    </h1>
                </motion.div>

                {/* Training Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
                    {trainingPackages.map((pkg, index) => {
                        // Skip rendering if program is not available
                        if (!pkg.program) return null;

                        const IconLabel = pkg.icon;
                        
                        // Check if early bird is active
                        const isEarlyBird = pkg.program?.is_early_bird_active === true;
                        const displayPrice = pkg.program?.formatted_current_price ?? pkg.program?.formatted_price;
                        const normalPrice = pkg.program?.formatted_price;
                        const earlyBirdEndFormatted = pkg.program?.is_early_bird_active && pkg.program?.early_bird_ends_at
                            ? new Date(pkg.program.early_bird_ends_at).toLocaleDateString(undefined, {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                            })
                            : null;

                        return (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.2 }}
                                viewport={{ once: true }}
                                className="group w-full"
                            >
                                <div className="bg-[#11182766] backdrop-blur-md border border-white/20 rounded-2xl p-4 md:p-6 lg:p-8 hover:border-[#9FCEF5]/50 transition-all duration-300">
                                    {/* Program Label */}
                                    <div className="text-center mb-4">
                                        <div className="inline-flex items-center gap-2 bg-[#9FCEF5]/20 px-4 py-2 rounded-full">
                                            <IconLabel className="w-5 h-5 text-[#9FCEF5]" />
                                            <span className="text-[#9FCEF5] font-semibold text-sm">{pkg.label}</span>
                                        </div>
                                    </div>

                                    {/* Limited Tickets */}
                                    <div className="text-center mb-6">
                                        <p className="text-gray-300 text-sm font-medium">* Limited spots available</p>
                                    </div>

                                    {/* Duration */}
                                    <div className="text-center mb-8">
                                        <div className="flex flex-col items-center">
                                            <div className="text-6xl md:text-7xl font-black text-white leading-none">
                                                {pkg.duration.split(' ')[0]}
                                            </div>
                                            <div className="text-lg md:text-2xl font-bold text-white/80 mt-1">
                                                {pkg.duration.split(' ').slice(1).join(' ')}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Features List */}
                                    <div className="space-y-4 mb-8">
                                        {pkg.features.map((feature, featureIndex) => (
                                            <div key={featureIndex} className="flex items-center text-white/90 group-hover:text-white transition-colors">
                                                <div className="w-6 h-6 mr-4 flex-shrink-0">
                                                    <feature.icon className="w-full h-full text-[#9FCEF5]" />
                                                </div>
                                                <span className="text-sm md:text-base font-medium">{feature.text}</span>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Terms Agreement */}
                                    <div className="mb-6">
                                        <label className="flex items-center cursor-pointer group/checkbox">
                                            <div className="relative mt-1">
                                                <input
                                                    type="checkbox"
                                                    checked={index === 0 ? agreement1 : agreement2}
                                                    onChange={e => (index === 0 ? setAgreement1(e.target.checked) : setAgreement2(e.target.checked))}
                                                    className="sr-only"
                                                />
                                                <div
                                                    className={`w-5 h-5 rounded border-2 ${
                                                        (index === 0 ? agreement1 : agreement2)
                                                            ? "bg-[#9FCEF5] border-[#9FCEF5]"
                                                            : "border-white/50 group-hover/checkbox:border-[#9FCEF5]"
                                                    } transition-colors flex items-center justify-center`}
                                                >
                                                    {(index === 0 ? agreement1 : agreement2) && <Check className="w-3 h-3 text-[#003C71]" />}
                                                </div>
                                            </div>
                                            <span className="ml-3 text-sm text-white/80 group-hover/checkbox:text-white transition-colors">
                                                I agree to the <Link to="/terms" className="text-[#9FCEF5] hover:text-[#7BB4E7] transition-colors underline">Terms of use</Link>
                                            </span>
                                        </label>
                                    </div>

                                    {/* Price */}
                                    <div className="text-center mb-2">
                                        <div className="text-3xl md:text-4xl font-black text-white">{displayPrice}</div>
                                        {isEarlyBird && (
                                            <div className="mt-2 space-y-1">
                                                <p className="text-gray-400 text-sm line-through">
                                                    Normal price {normalPrice}
                                                </p>
                                                {earlyBirdEndFormatted && (
                                                    <p className="text-[#8B1538] text-xs font-semibold">
                                                        Early bird until {earlyBirdEndFormatted}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Enroll Button */}
                                    <button
                                        onClick={() => {
                                            if (index === 0 ? agreement1 : agreement2) {
                                                handleEnrollNow(pkg.program);
                                            }
                                        }}
                                        className={`w-full bg-gradient-to-r from-[#8B1538] to-[#A51C30] text-white py-4 px-6 rounded-xl font-bold text-base flex items-center justify-center transition-all duration-300 ${
                                            (index === 0 ? agreement1 : agreement2)
                                                ? "hover:from-[#A51C30] hover:to-[#8B1538] hover:scale-105 hover:shadow-xl"
                                                : "opacity-50 cursor-not-allowed"
                                        }`}
                                        disabled={!(index === 0 ? agreement1 : agreement2)}
                                    >
                                        <GraduationCap className="mr-3 h-5 w-5" />
                                        ENROLL NOW
                                    </button>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default Training;

